/**
 * cart-tracking.js
 * ─────────────────────────────────────────────────────────────────
 * GA4 + Smartech tracking for add/remove cart events.
 * Listens to: variant:added | cart:updated
 *
 * No dependency on cart-shared.js — uses its own /cart.js fetch
 * so tracking is isolated and never contaminates the shared cache.
 * ─────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  const BASE_URL = 'https://plumgoodness.com/products/';

  const GA4_EVENTS = {
    add:    'GA4_add_to_cart',
    remove: 'remove_from_cart',
  };

  const SMARTECH_EVENTS = {
    add:    'add to cart custom',
    remove: 'remove from cart custom',
  };

  // ─── State ────────────────────────────────────────────────────────────────

  let previousCartMap        = null;
  let lastCartFingerprint    = '';
  let variantAddedInProgress = false;

  // ─── Utility ─────────────────────────────────────────────────────────────

  function getFormattedDateCart() {
    const d = new Date();
    return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
  }

  function toCamelCase(str) {
    if (!str) return '';
    return str.trim().split(/\s+/).map((word, i) =>
      i === 0
        ? word.charAt(0).toLowerCase() + word.slice(1).toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join('');
  }

  function normalizeProduct(data) {
    return {
      id:           parseInt(data.product_id ?? data.id, 10),
      title:        data.product_title ?? data.title,
      vendor:       data.vendor,
      variant:      data.variant_title,
      price:        parseFloat(data.price / 100),
      image:        data.image,
      handle:       data.handle,
      product_type: data.product_type,
    };
  }

  function buildCartMap(items) {
    return items.reduce((map, item) => {
      map[item.variant_id] = item;
      return map;
    }, {});
  }

  function cartFingerprint(items) {
    return items.map((i) => `${i.variant_id}:${i.quantity}`).sort().join('|');
  }

  // ─── Category resolver ────────────────────────────────────────────────────

  function getCategoriesFromProduct(item) {
    const tags    = item.tags ?? [];
    const tagList = Array.isArray(tags)
      ? tags
      : tags.split(',').map(t => t.trim()).filter(Boolean);

    const mainTag = tagList.find(t => t.toUpperCase().startsWith('MAINCATEGORY_'));
    const subTag  = tagList.find(t => t.toUpperCase().startsWith('SUBCATEGORY_'));

    return {
      parentCategory: mainTag
        ? toCamelCase(mainTag.split('_')[1])
        : toCamelCase(item.product_type) ?? '',
      subCategory: subTag
        ? toCamelCase(subTag.split('_')[1])
        : '',
    };
  }

  // Always fetch from /products/{handle}.js — guaranteed to have tags
  async function getCategoriesForVariant(item) {
    try {
      const handle  = item.handle ?? item.url?.split('/products/')[1];
      const product = await fetch(`/products/${handle}.js`).then(r => r.json());
      return getCategoriesFromProduct(product);
    } catch (_) {
      return { parentCategory: toCamelCase(item.product_type) ?? '', subCategory: '' };
    }
  }

  // ─── Payload builders ─────────────────────────────────────────────────────

  function buildGA4Payload(product, parentCategory, subCategory, quantity) {
    return {
      item_id:        product.id,
      item_name:      product.title,
      item_brand:     product.vendor,
      item_variant:   product.variant,
      item_category:  parentCategory,
      item_category2: subCategory,
      price:          product.price,
      currency:       'INR',
      image:          product.image,
      quantity,
    };
  }

  function buildSmartechPayload(product, parentCategory, subCategory, quantity) {
    const productUrl = BASE_URL + product.handle;
    return {
      prid:          product.id,
      name:          product.title,
      brand:         product.vendor,
      activity_date: getFormattedDateCart(),
      variant:       product.variant,
      prqt:          quantity,
      price:         product.price,
      currency:      'INR',
      image:         product.image,
      produrl:       productUrl,
      'refrer url':  productUrl,
      producttype:   product.product_type,
      pagetype:      window.location.href,
      category:      parentCategory,
      subcategory:   subCategory,
    };
  }

  // ─── Core tracker ─────────────────────────────────────────────────────────

  function trackCartEvent(action, rawProductData, parentCategory, subCategory, quantity = 1) {
    if (!GA4_EVENTS[action]) return;

    const product    = normalizeProduct(rawProductData);
    const ga4Payload = buildGA4Payload(product, parentCategory, subCategory, quantity);
    const smtPayload = buildSmartechPayload(product, parentCategory, subCategory, quantity);

    dataLayer.push({ ecommerce: null });
    dataLayer.push({ event: GA4_EVENTS[action], ecommerce: { items: [ga4Payload] } });

    smartech('dispatch', SMARTECH_EVENTS[action], smtPayload);
  }

  // ─── Cart diff ────────────────────────────────────────────────────────────

  async function diffAndTrackCart(newItems) {
    const fingerprint = cartFingerprint(newItems);

    if (fingerprint === lastCartFingerprint) return;

    // variant:added already fired for this add — just sync state
    if (variantAddedInProgress) {
      previousCartMap     = buildCartMap(newItems);
      lastCartFingerprint = fingerprint;
      return;
    }

    lastCartFingerprint = fingerprint;
    const newCartMap    = buildCartMap(newItems);

    if (previousCartMap === null) { previousCartMap = newCartMap; return; }

    // Items added or qty increased
    for (const item of newItems) {
      const prevQty = previousCartMap[item.variant_id]?.quantity ?? 0;
      const delta   = item.quantity - prevQty;
      if (delta === 0) continue;
      const { parentCategory, subCategory } = await getCategoriesForVariant(item);
      if (delta > 0) {
        trackCartEvent('add',    item, parentCategory, subCategory, delta);
      } else {
        trackCartEvent('remove', item, parentCategory, subCategory, Math.abs(delta));
      }
    }

    // Variant fully removed
    for (const variantId of Object.keys(previousCartMap)) {
      if (!newCartMap[variantId]) {
        const removed = previousCartMap[variantId];
        const { parentCategory, subCategory } = await getCategoriesForVariant(removed);
        trackCartEvent('remove', removed, parentCategory, subCategory, removed.quantity);
      }
    }

    previousCartMap = newCartMap;
  }

  // ─── Init ─────────────────────────────────────────────────────────────────

  function initCart() {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const items         = cart.items ?? [];
        previousCartMap     = buildCartMap(items);
        lastCartFingerprint = cartFingerprint(items);
      })
      .catch(() => {
        previousCartMap     = {};
        lastCartFingerprint = '';
      });
  }

  // ─── Event listeners ──────────────────────────────────────────────────────

  document.addEventListener('variant:added', async function (event) {
    variantAddedInProgress = true;
    try {
      const variant                         = event.detail.variant;
      const { parentCategory, subCategory } = await getCategoriesForVariant(variant);
      trackCartEvent('add', variant, parentCategory, subCategory);
    } finally {
      variantAddedInProgress = false;
    }
  });

  document.addEventListener('cart:updated', async function (event) {
    await diffAndTrackCart(event.detail.cart.items ?? []);
  });

  // ─── Public API ───────────────────────────────────────────────────────────

  window.CartTracking = {
    trackCartEvent,
    diffAndTrackCart,
    getCategoriesFromProduct,
    getCategoriesForVariant,
  };

  initCart();
})();
